import type { Dispatch, SetStateAction } from 'react'

import type { ColumnDef } from '@tanstack/react-table'
import type { FieldValues } from 'react-hook-form'

import EditIcon from '@/assets/icons/icon-edit.svg'
import RemoveIcon from '@/assets/icons/icon-trash.svg'
import { InfoQuickView } from '@/components/ui/table/components/InfoQuickView.tsx'
import { TooltipWithList } from '@/components/ui/table/components/tooltip-with-list.tsx'
import { TreeTable } from '@/components/ui/table/expandable-dnd-table/TreeTable'
import type { Entry } from '@/components/ui/table/expandable-dnd-table/useTreeTable'
import { useTreeTable } from '@/components/ui/table/expandable-dnd-table/useTreeTable'
import { EditSequenceTableActionsDropdown } from '@/modules/message/components/sequence/edit-sequence-table-actions-dropdown.tsx'
import type { GeneratePreviewIndicators } from '@/modules/message/components/sequence/message-sequence-editor.tsx'
import type { Message } from '@/modules/online-mass-messages/types.ts'
import { classNames } from '@/utils/helpers'

export type MessageBase = FieldValues & {
  id?: number
  text?: string
  fallbackText?: string
  order?: number
}

export interface DefineColumnOptions<T extends MessageBase> {
  onRemove?: (item: T) => void
  onEdit?: (item: T) => void
  disabled?: boolean
  ordered?: boolean
  actionsDropDown?: boolean
  generatePreviewIndicators?: GeneratePreviewIndicators<T>
}

export function listColumns<T extends MessageBase>(
  props: DefineColumnOptions<T>,
): ColumnDef<Entry<T>>[] {
  const {
    onRemove,
    onEdit,
    ordered,
    disabled,
    actionsDropDown,
    generatePreviewIndicators,
  } = props
  const columns: ColumnDef<Entry<T>>[] = []

  if (ordered) {
    columns.push(
      {
        accessorKey: 'order',
        header: 'Order',
        cell: ({ row }) => row.original.sorting + 1,
      },
      {
        id: 'orderChanger',
        header: '',
        cell: () => null,
      },
    )
  }
  columns.push(
    {
      accessorKey: 'text',
      header: 'Message',
      cell: ({ row }) => (
        <TooltipWithList
          messages={[row.original] as unknown as Message[]}
          mediaOptions={[]}
        />
      ),
    },
    {
      id: 'actions',
      header: () => <div className="flex justify-end">Actions</div>,
      cell: ({ row }) => {
        const indicators = generatePreviewIndicators?.(row.original)

        return (
          <div className="flex justify-end gap-4">
            {indicators && <InfoQuickView data={[indicators]} />}
            {actionsDropDown ? (
              <EditSequenceTableActionsDropdown
                disabled={!!disabled}
                onEdit={() => {
                  if (disabled) return
                  onEdit?.(row.original)
                }}
                onRemove={() => {
                  if (disabled) return
                  onRemove?.(row.original)
                }}
              />
            ) : (
              <>
                <EditIcon
                  width={18}
                  height={18}
                  className={classNames('text-primary-500 cursor-pointer', {
                    'opacity-50': disabled,
                  })}
                  onClick={() => {
                    if (disabled) return
                    onEdit?.(row.original)
                  }}
                >
                  Edit
                </EditIcon>
                <RemoveIcon
                  width={18}
                  height={18}
                  className={classNames('text-danger-default cursor-pointer', {
                    'opacity-50': disabled,
                  })}
                  onClick={() => {
                    if (disabled) return
                    onRemove?.(row.original)
                  }}
                >
                  Remove
                </RemoveIcon>
              </>
            )}
          </div>
        )
      },
    },
  )

  return columns
}

interface MessageSequenceEditorTable<T extends MessageBase> {
  columns: ColumnDef<Entry<T>>[]
  data: Entry<T>[]
  onDataChange: Dispatch<SetStateAction<Entry<T>[]>>
  disableDrag?: boolean
}

export const MessageSequenceEditorTable = <T extends MessageBase>(
  props: MessageSequenceEditorTable<T>,
) => {
  const { columns, data, onDataChange, disableDrag } = props

  const table = useTreeTable<T>({ data, columns })

  return (
    <TreeTable
      table={table}
      columns={columns}
      onChange={onDataChange}
      disableDrag={disableDrag}
    />
  )
}
